/**
 * Delayed-purchase rules as RffConditional / rffEvaluateFieldCondition rule
 * groups — the declarative twin of `isPurchaseLike` / `isDelayedPurchase` in
 * ./delayedPurchase.js, for forms whose visibility is data (RffConditional
 * `condition`, EditableDataRow `condition`, RffFieldsRenderer `f.condition`).
 *
 * The evaluator already nests AND / OR groups, so "cash-out AND flag" needs no
 * new operator — just a group. Every builder takes an optional `prefix` for
 * forms whose loan fields sit under a path (e.g. "loan.").
 */
import {
  CASH_OUT_REFI,
  DELAYED_PURCHASE_FIELD,
  LEGACY_DELAYED_PURCHASE,
} from "./delayedPurchase";

const NO_CASH_OUT_REFI = "Refinance - No Cash Out";

/** Purpose is "Refinance - Cash Out" — the only purpose the flag applies to. */
export const canBeDelayedPurchaseCondition = (prefix = "") => ({
  operator: "AND",
  rules: [
    { field: `${prefix}loanPurpose`, operator: "==", value: CASH_OUT_REFI },
  ],
});

/** `isDelayedPurchase(purpose, flag)` — legacy string, or cash-out + flag. */
export const delayedPurchaseCondition = (prefix = "") => ({
  operator: "OR",
  rules: [
    {
      field: `${prefix}loanPurpose`,
      operator: "==",
      value: LEGACY_DELAYED_PURCHASE,
    },
    {
      operator: "AND",
      rules: [
        { field: `${prefix}loanPurpose`, operator: "==", value: CASH_OUT_REFI },
        {
          field: `${prefix}${DELAYED_PURCHASE_FIELD}`,
          operator: "==",
          value: true,
        },
      ],
    },
  ],
});

/** `isPurchaseLike(purpose, flag)` — Purchase, or a delayed purchase. */
export const purchaseLikeCondition = (prefix = "") => ({
  operator: "OR",
  rules: [
    { field: `${prefix}loanPurpose`, operator: "==", value: "Purchase" },
    delayedPurchaseCondition(prefix),
  ],
});

/**
 * A TRUE refinance — what the two refinance purposes meant before the flag:
 * No Cash Out, or Cash Out that is NOT a delayed purchase. (The legacy
 * "Delayed Purchase" string never matched a refinance list, so it doesn't here.)
 */
export const trueRefinanceCondition = (prefix = "") => ({
  operator: "OR",
  rules: [
    { field: `${prefix}loanPurpose`, operator: "==", value: NO_CASH_OUT_REFI },
    {
      operator: "AND",
      rules: [
        { field: `${prefix}loanPurpose`, operator: "==", value: CASH_OUT_REFI },
        {
          field: `${prefix}${DELAYED_PURCHASE_FIELD}`,
          operator: "!=",
          value: true,
        },
      ],
    },
  ],
});
